import { useEffect, useReducer } from "react";

import { Switch } from "@headlessui/react";
import PropTypes from "prop-types";

import convertRate from "common/source-function/convertRate";
import numberFormat from "common/source-function/numberFormat";
import scanPrice from "common/source-function/scanPrice";
import totalPrice from "common/source-function/totalPrice";

import ButtonNew from "components/atoms/ButtonNew/index";
import CheckContent from "components/atoms/CheckContent";
import TabListNew from "components/molecules/TabListNew/index";

import AccountingTab from "./AccountingTab";
import AuditingTab from "./AuditingTab";

import styles from "./Index.module.scss";
/* ********** */
const tabs = [
  { id: 1, name: "Accounting" },
  { id: 2, name: "Auditing" },
];

const initState = {
  tabIndex: 0,
  paymentType: "yearly",
  accountingValue: 1,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fee_update_state":
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
};

const ServiceFeeCalculator = ({ services }) => {
  const priceAccountingPerMonth = (
    pricing,
    transaction,
    xero = 0,
    month = 1
  ) => {
    const price = scanPrice(pricing, transaction);

    return (parseFloat(price) + parseFloat(xero)) * month;
  };

  const priceAuditing = (
    pricing,
    revenue,
    exchangeRate = 1,
    originExchangeRate = 1,
    numberFormat = 1,
    rounded = false,
    pricingLevel = false
  ) => {
    return parseFloat(
      scanPrice(
        pricing,
        revenue,
        exchangeRate,
        originExchangeRate,
        numberFormat,
        rounded,
        pricingLevel
      )
    );
  };

  const [state, dispatch] = useReducer(reducer, {
    ...initState,
    accountingService: services[0],
    auditingService: services[1],
    auditingIndexType: 0,
    revenueMin: convertRate(
      services[1]?.revenue?.min,
      services[1]?.types[0]?.exchangeRate,
      services[1]?.exchangeRate,
      services[1]?.types[0]?.format?.value
    ),
    revenueMax: convertRate(
      services[1]?.revenue?.max,
      services[1]?.types[0]?.exchangeRate,
      services[1]?.exchangeRate,
      services[1]?.types[0]?.format?.value
    ),
    revenue: convertRate(
      services[1]?.revenue?.min,
      services[1]?.types[0]?.exchangeRate,
      services[1]?.exchangeRate,
      services[1]?.types[0]?.format?.value
    ),
    xero: { ...services[0]?.xero, checked: services[0]?.xero?.default },
  });

  const updateState = (payload) => {
    dispatch({
      type: "fee_update_state",
      payload,
    });
  };

  const setTabIndex = (index) => {
    updateState({
      tabIndex: index,
    });
  };

  const setPaymentType = (type) => {
    updateState({
      paymentType: type,
    });
  };

  const setAuditingIndexType = (newAuditingIndexType) => {
    const { auditingService } = state;
    const auditingType = auditingService?.types[newAuditingIndexType];

    updateState({
      auditingIndexType: newAuditingIndexType,
      revenueMin: convertRate(
        auditingService?.revenue?.min,
        auditingType?.exchangeRate,
        auditingService?.exchangeRate,
        auditingType?.format?.value
      ),
      revenueMax: convertRate(
        auditingService?.revenue?.max,
        auditingType?.exchangeRate,
        auditingService?.exchangeRate,
        auditingType?.format?.value
      ),
      revenue: convertRate(
        auditingService?.revenue?.min,
        auditingType?.exchangeRate,
        auditingService?.exchangeRate,
        auditingType?.format?.value
      ),
    });
  };

  const setRevenue = (value) => {
    const valueTemp =
      value === "" ? `${state.revenueMin}` : value.replaceAll(",", "");
    let valueNum = parseFloat(valueTemp);

    if (!isNaN(valueNum)) {
      if (valueNum > state.revenueMax) {
        valueNum = state.revenueMax;
      }
      if (valueNum < state.revenueMin) {
        valueNum = state.revenueMin;
      }
      updateState({
        revenue: valueNum,
      });
    }
  };

  const setXero = (checked) => {
    updateState({
      xero: { ...state.xero, checked },
    });
  };

  const setAccountingValue = (value) => {
    const valueTemp = value === "" ? "" : `${value.replaceAll(",", "")}`;
    const accType = state.accountingService?.types.find(
      (item) => item.key === state.paymentType
    );
    const accountingTemp = parseFloat(valueTemp);

    if (!Number.isNaN(accountingTemp) && accType) {
      if (
        accountingTemp >= accType.transaction?.min &&
        accountingTemp <= accType.transaction?.max
      ) {
        updateState({
          accountingValue: accountingTemp,
        });
      }
    } else if (value === "") {
      updateState({
        accountingValue: "",
      });
    }
  };

  useEffect(() => {
    const accAudit = document.getElementById("accounting-auditing");

    if (accAudit) {
      const tabs = document.querySelectorAll('[role="tab"]');
      const handle = (index) => {
        setTabIndex(index);
      };

      for (let i = 0; i < tabs.length; i++) {
        tabs[i].addEventListener("click", () => handle(i));
      }

      return () => {
        for (let i = 0; i < tabs.length; i++) {
          tabs[i].removeEventListener("click", () => handle(i));
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = window.location.hash;

    if (hash?.length > 0) {
      const scrollToTable = document.getElementById("accounting-auditing");

      window.scrollTo({
        top: scrollToTable.offsetTop - 160,
        behavior: "smooth",
      });
    }
  }, []);

  return (
    <div
      id="accounting-auditing"
      className="lg:shadow-bbc rounded-3xl lg:bg-white"
    >
      <div className="row">
        <div className="col-12 lg:col-6">
          <div className="pt-3 pb-7 lg:py-9">
            <TabListNew
              data={tabs.map((item) => item.name)}
              tabListClass={{
                padding: "p-1",
              }}
              tabClass={{
                padding: "px-4 py-1",
                tabColorActive: "text-white bg-bbc-brand-500",
              }}
              activeTab={0}
            />
            <div className="pt-10 lg:pt-8">
              {state.tabIndex === 0 && (
                <AccountingTab
                  setAccountingValue={setAccountingValue}
                  accountingValue={state.accountingValue}
                  accountingService={state.accountingService}
                  xero={state.xero}
                  setXero={setXero}
                />
              )}
              {state.tabIndex === 1 && (
                <AuditingTab
                  revenueMin={state.revenueMin}
                  revenueMax={state.revenueMax}
                  revenue={state.revenue}
                  setAuditingIndexType={setAuditingIndexType}
                  auditingIndexType={state.auditingIndexType}
                  auditingService={state.auditingService}
                  setRevenue={setRevenue}
                />
              )}
              {state.tabIndex === 0 && (
                <div className="flex flex-col items-center">
                  <p className="mb-4 text-center text-2xl font-semibold lg:text-lg">
                    What included in Accounting packages
                  </p>
                  {/* monthly */}
                  {state.paymentType === "monthly" && (
                    <ul className="w-full lg:w-auto">
                      {state.accountingService.types[0].content.map(
                        (item, index) => {
                          return (
                            <li
                              className="mb-1 flex items-center gap-3"
                              key={index}
                            >
                              <CheckContent data={item} />
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
                  {/* yearly */}
                  {state.paymentType === "yearly" && (
                    <ul className="w-full lg:w-auto">
                      {state.accountingService.types[1].content.map(
                        (item, index) => {
                          return (
                            <li
                              className="mb-1 flex items-center gap-3"
                              key={index}
                            >
                              <CheckContent data={item} />
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 lg:col-6">
          <div className="h-full rounded-3xl bg-bbc-gradient-3 py-8 px-6 lg:py-12 lg:px-11">
            {state.tabIndex === 0 && (
              <div className="mb-8 flex items-center justify-center gap-x-4">
                <div className="text-white">Monthly</div>
                <Switch
                  checked={state.paymentType === "yearly"}
                  onChange={(value) =>
                    setPaymentType(value ? "yearly" : "monthly")
                  }
                  className={`relative  inline-flex  cursor-pointer rounded-full border-transparent bg-white ${styles.switch}`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      state.paymentType === "yearly"
                        ? "translate-x-9"
                        : "translate-x-0"
                    } ${
                      styles.paymentType
                    } inline-block rounded-full bg-bbc-primary  transition duration-200 ease-in-out`}
                  />
                </Switch>
                <div className="text-white">Yearly</div>
              </div>
            )}

            <div className="mb-8 flex items-end justify-center">
              <p className="text-5xl font-semibold text-white">
                {state.tabIndex === 0 && (
                  <>
                    {state.accountingService?.currencySymbol}
                    {numberFormat(
                      priceAccountingPerMonth(
                        state.accountingService?.types.find(
                          (item) => item.key === state.paymentType
                        )?.pricing,
                        state.accountingValue,
                        state.xero.checked ? state.xero.value : 0
                      )
                    )}
                  </>
                )}
                {state.tabIndex === 1 && (
                  <>
                    {state.accountingService?.currencySymbol}
                    {numberFormat(
                      priceAuditing(
                        state.auditingService?.pricing,
                        state.revenue,
                        state.auditingService?.types[state.auditingIndexType]
                          ?.exchangeRate,
                        state.auditingService?.exchangeRate,
                        state.auditingService?.types[state.auditingIndexType]
                          ?.format?.value,
                        state.auditingService?.types[state.auditingIndexType]
                          ?.rounded,
                        state.auditingService?.pricingLevel
                      )
                    )}
                  </>
                )}
              </p>
              <p className="text-lg font-semibold text-white">
                {state.tabIndex === 0 ? "/Month" : ""}
              </p>
            </div>
            <p className="mb-1 text-center text-white">
              Total yearly estimated cost for{" "}
              {services[state.tabIndex]?.types[state.auditingIndexType]
                ?.costFor ? (
                <>
                  {state.revenue > 0
                    ? services[state.tabIndex]?.types[state.auditingIndexType]
                        ?.costFor
                    : state.accountingService?.name}
                </>
              ) : (
                services[state.tabIndex]?.name
              )}
              :
            </p>
            <p className="mb-8 text-center text-lg font-semibold text-white">
              {state.accountingService?.currencySymbol}
              {numberFormat(
                totalPrice([
                  priceAccountingPerMonth(
                    state.accountingService?.types.find(
                      (item) => item.key === state.paymentType
                    )?.pricing,
                    state.accountingValue,
                    state.xero.checked ? state.xero.value : 0,
                    12
                  ),
                  priceAuditing(
                    state.auditingService?.pricing,
                    state.revenue,
                    state.auditingService?.types[state.auditingIndexType]
                      ?.exchangeRate,
                    state.auditingService?.exchangeRate,
                    state.auditingService?.types[state.auditingIndexType]
                      ?.format?.value,
                    state.auditingService?.types[state.auditingIndexType]
                      ?.rounded,
                    state.auditingService?.pricingLevel
                  ),
                ])
              )}
            </p>
            <div className="mb-8 flex justify-center">
              <ButtonNew
                btnTextColor={"text-bbc-brand-500"}
                btnColor="bg-white"
                onClick={async () => {
                  // eslint-disable-next-line no-undef
                  await Tawk_API.toggle();
                }}
              >
                Talk To Us
              </ButtonNew>
            </div>

            <p className="text-center text-white">
              ** The above fee is in USD and may vary depending on the
              complexity
              <br />
              of your transaction
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceFeeCalculator;

ServiceFeeCalculator.propTypes = {
  services: PropTypes.array,
};

ServiceFeeCalculator.defaultProps = {
  services: [],
};
